import * as React from "react";

export interface SidebarIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
}

/**
 * Pfizer Logo - Main brand logo for sidebar header
 */
export function PfizerLogo({
  size = 32,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Pfizer Logo"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.231697 18.3512C1.28311 15.473 7.26874 14.2601 16.0003 15.2631L17.6917 9.33224C9.05161 8.28565 2.743 9.4631 1.69153 13.2135L0.231697 18.3512Z"
        fill="#0095FF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M31.7668 12.181C30.7154 15.0592 24.7318 16.2661 16.0003 15.2631L14.309 21.1939C22.9491 22.2405 29.2577 21.063 30.3091 17.3127L31.7668 12.181Z"
        fill="#0000C9"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.7598 5.93081C18.5142 6.80304 31.5434 12.821 30.3091 17.3127L31.4514 13.3008C31.8172 12.0361 32 11.382 32 10.8586C32 6.27969 19.4285 0.697762 7.45125 0L5.7598 5.93081Z"
        fill="#0095FF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.2402 24.5954C13.4858 23.7232 0.457278 17.7052 1.69153 13.2135L0.548562 17.2255C0.182835 18.4901 0 19.1443 0 19.6676C0 24.2465 12.5715 29.8285 24.5488 30.5263L26.2402 24.5954Z"
        fill="#0000C9"
      />
    </svg>
  );
}
PfizerLogo.displayName = "PfizerLogo";

/**
 * Home Icon - Navigation to home/dashboard
 */
export function HomeIcon({ size = 24, className, ...props }: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M13 17H19V7.978L12 2.534L5 7.978V17H11V11H13V17ZM21 18C21 18.2652 20.8946 18.5196 20.7071 18.7071C20.5196 18.8947 20.2652 19 20 19H4C3.73478 19 3.48043 18.8947 3.29289 18.7071C3.10536 18.5196 3 18.2652 3 18V7.49C2.99994 7.33764 3.0346 7.18723 3.10149 7.05029C3.16838 6.91336 3.26574 6.79361 3.386 6.7L11.386 0.478028C11.5615 0.34147 11.7776 0.267334 12 0.267334C12.2224 0.267334 12.4385 0.34147 12.614 0.478028L20.614 6.7C20.7343 6.79361 20.8316 6.91336 20.8985 7.05029C20.9654 7.18723 21.0001 7.33764 21 7.49V18Z"
        fill="currentColor"
      />
    </svg>
  );
}
HomeIcon.displayName = "HomeIcon";

/**
 * Generate Icon - Content generation/creation
 */
export function GenerateIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M14.381 4.07895C16.0904 4.07895 17.4762 2.70053 17.4762 1H18.9048C18.9048 2.70053 20.2906 4.07895 22 4.07895V5.5C20.2906 5.5 18.9048 6.87847 18.9048 8.57895H17.4762C17.4762 6.87847 16.0904 5.5 14.381 5.5V4.07895ZM3.90476 5.73684C3.90476 4.69035 4.75761 3.84211 5.80952 3.84211H12.4762V1.94737H5.80952C3.70556 1.94737 2 3.64398 2 5.73684V15.2105C2 17.3034 3.70556 19 5.80952 19H17.2381C19.342 19 21.0476 17.3034 21.0476 15.2105V10.4737H19.1429V15.2105C19.1429 16.257 18.2901 17.1053 17.2381 17.1053H5.80952C4.75761 17.1053 3.90476 16.257 3.90476 15.2105V5.73684Z"
        fill="currentColor"
      />
    </svg>
  );
}
GenerateIcon.displayName = "GenerateIcon";

/**
 * Agentic Planner Icon - AI chat/planner feature
 */
export function AgenticPlannerIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M11.3072 4.83443C11.5978 4.28443 12.4002 4.28463 12.691 4.83443L13.5019 6.36912C13.7912 6.91643 14.2494 7.36261 14.8125 7.64413L16.3903 8.43248C16.955 8.71581 16.956 9.50799 16.3903 9.79089L14.8125 10.5803C14.2493 10.8619 13.7911 11.3088 13.5019 11.8563L12.691 13.39C12.4003 13.9399 11.5978 13.94 11.3072 13.39L10.4981 11.8563C10.209 11.3089 9.75062 10.8619 9.1875 10.5803L7.60972 9.79089C7.04369 9.50807 7.04469 8.71581 7.60972 8.43248L9.1875 7.64413C9.75054 7.36253 10.209 6.91633 10.4981 6.36912L11.3072 4.83443Z"
        fill="currentColor"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.2681 0.4C18.7603 0.400358 21.5998 3.15772 21.6 6.56784V19.6L16.7869 17.3697C16.1293 17.0651 15.3898 16.9689 14.6728 17.0959L9.86436 17.9463C5.98324 18.6327 2.40044 15.7313 2.4 11.8778V6.56784C2.40023 3.15749 5.23964 0.400231 8.73193 0.4H15.2681ZM8.73193 2.13723C6.21097 2.13739 4.17388 4.12557 4.17381 6.56784V11.8778C4.17415 14.6369 6.74555 16.7329 9.54855 16.2372L14.356 15.3859C15.4378 15.1947 16.5539 15.3398 17.5463 15.7994L19.8244 16.8541V6.56784C19.8242 4.12553 17.7883 2.1373 15.2672 2.13723H8.73193Z"
        fill="currentColor"
      />
    </svg>
  );
}
AgenticPlannerIcon.displayName = "AgenticPlannerIcon";

/**
 * Upload Icon - File upload functionality
 */
export function UploadIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M4 17H20V10H22V18C22 18.2652 21.8946 18.5196 21.7071 18.7071C21.5196 18.8946 21.2652 19 21 19H3C2.73478 19 2.48043 18.8946 2.29289 18.7071C2.10536 18.5196 2 18.2652 2 18V10H4V17ZM13 7V14H11V7H6L12 1L18 7H13Z"
        fill="currentColor"
      />
    </svg>
  );
}
UploadIcon.displayName = "UploadIcon";

/**
 * Content Rule Center Icon - Content validation/rules
 */
export function ContentRuleCenterIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M12 20C6.477 20 2 15.523 2 10C2 4.477 6.477 0 12 0C17.523 0 22 4.477 22 10C22 15.523 17.523 20 12 20ZM12 18C14.1217 18 16.1566 17.1571 17.6569 15.6569C19.1571 14.1566 20 12.1217 20 10C20 7.87827 19.1571 5.84344 17.6569 4.34315C16.1566 2.84286 14.1217 2 12 2C9.87827 2 7.84344 2.84286 6.34315 4.34315C4.84286 5.84344 4 7.87827 4 10C4 12.1217 4.84286 14.1566 6.34315 15.6569C7.84344 17.1571 9.87827 18 12 18ZM11.003 14L6.76 9.757L8.174 8.343L11.003 11.172L16.659 5.515L18.074 6.929L11.003 14Z"
        fill="currentColor"
      />
    </svg>
  );
}
ContentRuleCenterIcon.displayName = "ContentRuleCenterIcon";

/**
 * Knowledge Center Icon - Documentation/help
 */
export function KnowledgeCenterIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M13 19V21H11V19H3C2.73478 19 2.48043 18.8946 2.29289 18.7071C2.10536 18.5196 2 18.2652 2 18V2C2 1.73479 2.10536 1.48043 2.29289 1.2929C2.48043 1.10536 2.73478 1 3 1H9C9.56763 0.999334 10.1288 1.11976 10.6461 1.35325C11.1635 1.58674 11.625 1.92792 12 2.354C12.375 1.92792 12.8365 1.58674 13.3539 1.35325C13.8712 1.11976 14.4324 0.999334 15 1H21C21.2652 1 21.5196 1.10536 21.7071 1.2929C21.8946 1.48043 22 1.73479 22 2V18C22 18.2652 21.8946 18.5196 21.7071 18.7071C21.5196 18.8946 21.2652 19 21 19H13ZM20 17V3H15C14.4696 3 13.9609 3.21071 13.5858 3.58579C13.2107 3.96086 13 4.46957 13 5V17H20ZM11 17V5C11 4.46957 10.7893 3.96086 10.4142 3.58579C10.0391 3.21071 9.53043 3 9 3H4V17H11Z"
        fill="currentColor"
      />
    </svg>
  );
}
KnowledgeCenterIcon.displayName = "KnowledgeCenterIcon";

/**
 * Intercom Icon - Support/chat button
 */
export function IntercomIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M19.5 2H4.5C3.12 2 2 3.12 2 4.5V19.5C2 20.88 3.12 22 4.5 22H19.5C20.88 22 22 20.88 22 19.5V4.5C22 3.12 20.88 2 19.5 2ZM7.5 15.5C7.5 15.78 7.28 16 7 16C6.72 16 6.5 15.78 6.5 15.5V10.5C6.5 10.22 6.72 10 7 10C7.28 10 7.5 10.22 7.5 10.5V15.5ZM10.5 17C10.5 17.28 10.28 17.5 10 17.5C9.72 17.5 9.5 17.28 9.5 17V9C9.5 8.72 9.72 8.5 10 8.5C10.28 8.5 10.5 8.72 10.5 9V17ZM13.5 17C13.5 17.28 13.28 17.5 13 17.5C12.72 17.5 12.5 17.28 12.5 17V9C12.5 8.72 12.72 8.5 13 8.5C13.28 8.5 13.5 8.72 13.5 9V17ZM16.5 17C16.5 17.28 16.28 17.5 16 17.5C15.72 17.5 15.5 17.28 15.5 17V9C15.5 8.72 15.72 8.5 16 8.5C16.28 8.5 16.5 8.72 16.5 9V17ZM17.5 15.5C17.5 15.78 17.28 16 17 16C16.72 16 16.5 15.78 16.5 15.5V10.5C16.5 10.22 16.72 10 17 10C17.28 10 17.5 10.22 17.5 10.5V15.5Z"
        fill="currentColor"
      />
    </svg>
  );
}
IntercomIcon.displayName = "IntercomIcon";

/**
 * Image Generation Icon - AI image generation feature
 */
export function ImageGenerationIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M20 5H4V19L8.5 14.5L11.5 17.5L16 13L20 17V5ZM20 3C20.5304 3 21.0391 3.21071 21.4142 3.58579C21.7893 3.96086 22 4.46957 22 5V19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H20ZM8.5 8C9.03043 8 9.53914 8.21071 9.91421 8.58579C10.2893 8.96086 10.5 9.46957 10.5 10C10.5 10.5304 10.2893 11.0391 9.91421 11.4142C9.53914 11.7893 9.03043 12 8.5 12C7.96957 12 7.46086 11.7893 7.08579 11.4142C6.71071 11.0391 6.5 10.5304 6.5 10C6.5 9.46957 6.71071 8.96086 7.08579 8.58579C7.46086 8.21071 7.96957 8 8.5 8Z"
        fill="currentColor"
      />
    </svg>
  );
}
ImageGenerationIcon.displayName = "ImageGenerationIcon";

/**
 * Image Generation Empty Icon - Large icon for empty state
 * This icon has fixed 66.7x60 dimensions by default
 */
export interface ImageGenerationEmptyIconProps extends React.SVGProps<SVGSVGElement> {
  width?: number | string;
  height?: number | string;
}

export function ImageGenerationEmptyIcon({
  width = 66.7,
  height = 60,
  className,
  ...props
}: ImageGenerationEmptyIconProps) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 67 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M37.4881 14.5287H5.7674V54.2676L34.6044 25.8827L51.9066 42.9136V28.7212H57.674V57.1255C57.6687 57.8709 57.3644 58.5856 56.8291 59.113C56.294 59.6397 55.5696 59.9386 54.8128 59.9446H8.15546L8.09632 60L8.03718 59.9446H2.86117C2.10222 59.9438 1.37277 59.647 0.836385 59.1185C0.300238 58.59 -3.84752e-07 57.8723 0 57.1255V11.6708C0.0052773 10.9254 0.309583 10.2107 0.844834 9.68331C1.37999 9.15654 2.10433 8.85766 2.86117 8.85172H37.4881V14.5287ZM13.9229 54.2676H51.9066V50.9412L34.6044 33.9103L13.9229 54.2676Z"
        fill="currentColor"
      />
      <path
        d="M17.3022 20.2057C18.8318 20.2057 20.2983 20.8042 21.3799 21.8689C22.4615 22.9335 23.0696 24.377 23.0696 25.8827C23.0696 27.3883 22.4615 28.8318 21.3799 29.8965C20.2983 30.9611 18.8318 31.5596 17.3022 31.5596C15.7726 31.5596 14.3061 30.9611 13.2245 29.8965C12.1429 28.8318 11.5348 27.3883 11.5348 25.8827C11.5348 24.377 12.1429 22.9335 13.2245 21.8689C14.3061 20.8042 15.7726 20.2057 17.3022 20.2057Z"
        fill="currentColor"
      />
      <path
        d="M52.0858 1.85343C53.0148 -0.617811 56.5658 -0.617811 57.4948 1.85343L59.0033 5.86623C59.2954 6.64318 59.9177 7.25576 60.707 7.54325L64.7837 9.02812C67.2943 9.94257 67.2943 13.4378 64.7837 14.3523L60.707 15.8372C59.9177 16.1247 59.2954 16.7372 59.0033 17.5142L57.4948 21.527C56.5658 23.9982 53.0148 23.9982 52.0858 21.527L50.5773 17.5142C50.2852 16.7372 49.6629 16.1247 48.8735 15.8372L44.7968 14.3523C42.2862 13.4378 42.2862 9.94257 44.7968 9.02812L48.8735 7.54326C49.6629 7.25576 50.2852 6.64318 50.5773 5.86623L52.0858 1.85343Z"
        fill="currentColor"
      />
    </svg>
  );
}
ImageGenerationEmptyIcon.displayName = "ImageGenerationEmptyIcon";

/**
 * Document Icon - Brand guidelines/document link
 */
export function DocumentIcon({
  size = 24,
  className,
  ...props
}: SidebarIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 9 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <path
        d="M9 1H2C1.73478 1 1.48043 1.10536 1.29289 1.29289C1.10536 1.48043 1 1.73478 1 2C1 2.26522 1.10536 2.51957 1.29289 2.70711C1.48043 2.89464 1.73478 3 2 3H9V9.5C9 9.63261 8.94732 9.75979 8.85355 9.85355C8.75979 9.94732 8.63261 10 8.5 10H2C1.46957 10 0.960859 9.78929 0.585786 9.41421C0.210714 9.03914 0 8.53043 0 8V2C0 1.46957 0.210714 0.960859 0.585786 0.585786C0.960859 0.210714 1.46957 0 2 0H8.5C8.63261 0 8.75979 0.0526784 8.85355 0.146447C8.94732 0.240215 9 0.367392 9 0.5V1ZM1 8C1 8.26522 1.10536 8.51957 1.29289 8.70711C1.48043 8.89464 1.73478 9 2 9H8V4H2C1.64891 4.00056 1.30392 3.90828 1 3.7325V8ZM8.5 2.5H2C1.86739 2.5 1.74021 2.44732 1.64645 2.35355C1.55268 2.25979 1.5 2.13261 1.5 2C1.5 1.86739 1.55268 1.74021 1.64645 1.64645C1.74021 1.55268 1.86739 1.5 2 1.5H8.5V2.5Z"
        fill="currentColor"
      />
    </svg>
  );
}
DocumentIcon.displayName = "DocumentIcon";
